<template>
  <!--
    The column template is an inline style, not a Tailwind arbitrary value. As a class
    it silently produced no rule, the grid stayed one column, and the preview laid out
    below the whole result list — open, correct, and ~4600px off screen.
  -->
  <div class="grid" :style="{ gridTemplateColumns: gridColumns }">
    <div class="min-w-0 px-6 pb-8 pt-0 print:!px-0 print:!py-0">
      <ConflictUpgradeNotice />

      <div class="flex flex-wrap items-center justify-between gap-4">
        <h1 class="mt-0 text-2xl font-normal">Conflict check</h1>
        <div
          v-if="view === 'new' && canActOnResults"
          class="flex items-center gap-3 print:hidden"
        >
          <span
            class="text-xs text-gray-500"
            data-cy="conflict-unsaved-indicator"
          >
            Not saved
          </span>
          <LlSecondaryButton data-cy="conflict-print" @click="print">
            Print
          </LlSecondaryButton>
          <LlSecondaryButton
            color="primary"
            :loading="store.saving"
            :disabled="notesOverLimit"
            data-cy="conflict-save"
            @click="saveCheck"
          >
            Save check
          </LlSecondaryButton>
        </div>
      </div>

      <div class="print:hidden">
        <LlTabs
          :model-value="
            view === 'new' ? CONFLICT_TAB_NEW : CONFLICT_TAB_PREVIOUS
          "
          :items="conflictTabs"
          class="mb-2"
          data-cy="conflict-tabs"
          @select="(tab) => openTab(String(tab))"
        >
          <template #label="{ item }">
            {{ item.label }}
            <span
              v-if="item.value === CONFLICT_TAB_PREVIOUS && previousCount !== null"
              class="ml-1.5 text-xs"
            >
              ({{ previousCount }})
            </span>
          </template>
        </LlTabs>
      </div>

      <SavedConflictCheckView
        v-if="view === 'saved'"
        ref="savedView"
        @back="openTab(CONFLICT_TAB_PREVIOUS)"
        @retry="loadSavedCheck"
        @print="print"
        @open="openSavedPreview"
        @dirty="uiState.savedNotesDirty = $event"
      />

      <ConflictCheckLog
        v-else-if="view === 'previous'"
        :check-href="checkHref"
        @open="openSavedCheck"
        @new="openTab(CONFLICT_TAB_NEW)"
      />

      <template v-else>
        <p class="mb-4 mt-1 max-w-[64ch] text-sm text-gray-500 print:hidden">
          Enter every party involved. Results name the field that matched, the
          word that matched it, and the name it came from.
        </p>

        <ConflictPrintMasthead v-if="store.hasRun" :sheet="unsavedSheet" />

        <ConflictQueryPanel
          v-if="showQueryPanel"
          v-model="store.query"
          v-model:include-time-entries="store.includeTimeEntries"
          class="print:hidden"
          :parties="parties"
          :party-count="partyCount"
          :running="store.running"
          :skipped-parties="store.result?.skippedParties ?? []"
          :ignored-role-words="ignoredRoleWords"
          @remove-party="onRemoveParty"
          @run="run"
        />

        <!-- Once a check has run the panel folds to one line; refining reopens it with
           the names intact, because a seven-party query is edited, not retyped. -->
        <v-card
          v-else
          class="flex flex-wrap items-center gap-3.5 border border-gray-200 px-4 py-3"
          elevation="0"
          data-cy="conflict-summary-strip"
        >
          <span class="font-medium">
            {{ store.searchableParties.length }}
            {{ store.searchableParties.length === 1 ? 'name' : 'names' }}
            checked
          </span>
          <span class="text-xs text-gray-500 [overflow-wrap:anywhere]">
            {{ store.searchableParties.map((p) => p.text).join(' · ') }}
          </span>
          <span class="text-xs text-gray-500">
            {{
              store.ranWithTimeEntries
                ? 'Time entries included'
                : 'Time entries not searched'
            }}
          </span>
          <LlSecondaryButton
            class="ml-auto print:hidden"
            size="small"
            data-cy="conflict-refine"
            @click="refine"
          >
            Refine search
          </LlSecondaryButton>
        </v-card>

        <v-skeleton-loader
          v-if="store.running"
          class="mt-5"
          type="list-item-three-line@4"
          data-cy="conflict-running"
        />

        <!--
        A failed check and a clean one look alike and mean opposite things. This is the
        one output the feature must never get wrong, so the error state is red, says the
        check did not run, and never borrows the clean wording.
      -->
        <div
          v-else-if="store.failed"
          class="mt-5 rounded border border-l-4 border-[rgba(var(--v-theme-error),0.3)] bg-[rgba(var(--v-theme-error),0.08)] px-3.5 py-3 text-xs leading-relaxed"
          data-cy="conflict-error"
        >
          <span class="font-medium text-red-700"
            >The conflict check did not run.</span
          >
          Nothing was searched, so nothing here says these names are clear.
          <div class="mt-2.5">
            <LlSecondaryButton
              size="small"
              data-cy="conflict-retry"
              @click="run"
            >
              Try again
            </LlSecondaryButton>
          </div>
        </div>

        <template v-else-if="store.hasRun">
          <div
            v-if="store.result?.truncated"
            class="mt-5 rounded border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs"
            data-cy="conflict-truncated"
          >
            Only the first {{ store.result.partiesChecked }} of
            {{ store.result.partiesFound }} names were checked. Remove some
            names and run again for the rest.
          </div>

          <!-- Nothing matched anywhere: the one place the clean wording belongs. -->
          <div
            v-if="!store.allRows.length"
            class="mt-5 rounded border border-green-200 bg-green-50 px-3.5 py-3 text-xs leading-relaxed"
            data-cy="conflict-clean"
          >
            <span class="font-medium text-green-800">No conflicts found.</span>
            Searched {{ CONFLICT_SCOPE_SENTENCE
            }}{{
              store.ranWithTimeEntries
                ? ', and time entry descriptions.'
                : '. Time entries were not searched.'
            }}
            Invoice narratives are not searched.
            <div v-if="!store.ranWithTimeEntries" class="mt-2.5">
              <LlSecondaryButton
                size="small"
                data-cy="conflict-rerun-with-entries"
                @click="rerunWithTimeEntries"
              >
                Run again including time entries
              </LlSecondaryButton>
            </div>
          </div>

          <!-- Results exist but the filter hides them all. Not a clean result. -->
          <div
            v-else-if="!store.visibleRowCount"
            class="mt-5 rounded border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs leading-relaxed"
            data-cy="conflict-all-filtered"
          >
            <span class="font-medium"
              >Every result is hidden by the field filter.</span
            >
            This check returned {{ store.allRows.length }}
            {{ store.allRows.length === 1 ? 'result' : 'results' }}. None of
            them are in the fields you have left selected.
            <div class="mt-2.5">
              <LlSecondaryButton size="small" @click="store.showAllFields">
                Show all fields
              </LlSecondaryButton>
            </div>
          </div>

          <ConflictResultList
            v-else
            ref="resultList"
            :full-matches="store.visibleFullMatches"
            :partial-matches="store.visiblePartialMatches"
            @open="store.openPreview"
          />

          <div class="mt-6 max-w-[76ch] print:hidden">
            <ConflictNotesField
              id="conflict-notes-input"
              v-model="store.notes"
              :disabled="store.saving"
            />
            <div
              v-if="store.saveError"
              class="mt-3 rounded border border-l-4 border-[rgba(var(--v-theme-error),0.3)] bg-[rgba(var(--v-theme-error),0.08)] px-3.5 py-3 text-xs leading-relaxed"
              data-cy="conflict-save-error"
            >
              <span class="font-medium text-red-700"
                >The check was not saved.</span
              >
              Your results and notes are still here. Try saving again.
            </div>
            <div class="mt-3 flex justify-end">
              <LlSecondaryButton
                color="primary"
                :loading="store.saving"
                :disabled="notesOverLimit"
                data-cy="conflict-save-bottom"
                @click="saveCheck"
              >
                Save check
              </LlSecondaryButton>
            </div>
          </div>

          <ConflictFieldFilter
            v-if="store.allRows.length"
            class="print:hidden"
            :field-counts="store.fieldCounts"
            :hidden-fields="store.hiddenFields"
            :hidden-row-count="store.hiddenRowCount"
            :permitted-fields="store.permittedFields"
            :included-time-entries="store.ranWithTimeEntries"
            @toggle="store.toggleField"
            @show-all="store.showAllFields"
          />

          <div class="print-only print:!mt-4">
            <div class="text-sm font-bold">Notes</div>
            <p
              v-if="store.notes.trim()"
              class="whitespace-pre-line text-sm [overflow-wrap:anywhere]"
            >
              {{ store.notes }}
            </p>
            <p v-else class="text-sm italic">No notes were recorded.</p>
          </div>
        </template>
      </template>
    </div>

    <ConflictPreviewPanel
      v-if="showPreview"
      class="sticky top-0 max-h-screen self-start print:hidden"
      :record="store.preview"
      :loading="store.previewLoading"
      :error="store.previewError"
      :included-time-entries="store.ranWithTimeEntries"
      :client-href="previewClientHref"
      :matter-href="previewMatterHref"
      @close="store.closePreview"
    />

    <LlConfirmationDialog
      ref="leaveDialog"
      title="Leave without saving?"
      message="Your notes on this check have not been saved. If you leave now they will be lost."
      confirm-text="Leave"
      toggle-text="Stay"
      data-cy="conflict-leave-dialog"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import RouteService from '@/services/RouteService';
import EventTracker from '@/services/eventTracker';
import {
  TrackingAction,
  TrackingCategory,
} from '@/constants/trackingConstants';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTabs from '@/components/LlTabs.vue';
import type { TabItem } from '@/components/LlTabs.vue';
import ConflictCheckLog from './components/ConflictCheckLog.vue';
import ConflictNotesField from './components/ConflictNotesField.vue';
import ConflictPrintMasthead from './components/ConflictPrintMasthead.vue';
import SavedConflictCheckView from './components/SavedConflictCheckView.vue';
import ConflictQueryPanel from './components/ConflictQueryPanel.vue';
import ConflictResultList from './components/ConflictResultList.vue';
import ConflictFieldFilter from './components/ConflictFieldFilter.vue';
import ConflictPreviewPanel from './components/ConflictPreviewPanel.vue';
import ConflictUpgradeNotice from './components/ConflictUpgradeNotice.vue';
import { useConflictCheckStore } from './stores/useConflictCheckStore';
import { useConflictCheckRecordStore } from './stores/useConflictCheckRecordStore';
import { useConflictParties } from './composables/useConflictParties';
import { CONFLICT_SCOPE_SENTENCE } from './constants/conflictFieldConstants';
import {
  CONFLICT_NOTES_MAX_LENGTH,
  CONFLICT_TAB_NEW,
  CONFLICT_TAB_PREVIOUS,
} from './constants/conflictRecordConstants';
import type {
  ConflictPrintSheet,
  ConflictResultRow,
} from './models/conflictTypes';

type StateChangeEvent = { preventDefault: () => void };
type RouteTarget = { name?: string };

const props = defineProps<{
  state?: Record<string, unknown>;
  stateParams?: Record<string, unknown>;
  rootScope?: Record<string, unknown>;
  tracking?: Record<string, unknown>;
}>();

const store = useConflictCheckStore();
const recordStore = useConflictCheckRecordStore();
const meStore = useMeStore();
const { me } = storeToRefs(meStore);
const { handleError, handleSuccess } = useSnackbarHandler();
const routeService = new RouteService(props);

const { query } = storeToRefs(store);
const { parties, partyCount, removeParty } = useConflictParties(query);

/** Reopened by "Refine search"; the query panel is otherwise hidden after a run. */
const queryPanelOpen = ref(!store.hasRun);
const resultList = ref<InstanceType<typeof ConflictResultList> | null>(null);
const savedView = ref<InstanceType<typeof SavedConflictCheckView> | null>(null);
const leaveDialog = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);

const uiState = reactive({ savedNotesDirty: false });

const savedCheckId = computed(() => {
  const id = props.stateParams?.check;
  return typeof id === 'string' && id ? id : null;
});

const view = computed<'new' | 'previous' | 'saved'>(() => {
  if (savedCheckId.value) return 'saved';
  return props.stateParams?.tab === CONFLICT_TAB_PREVIOUS ? 'previous' : 'new';
});

const canActOnResults = computed(
  () => store.hasRun && !store.running && !store.failed,
);
const notesOverLimit = computed(
  () => store.notes.length > CONFLICT_NOTES_MAX_LENGTH,
);

/** Only the unfiltered total is the firm's count; a filtered one would mislead. */
const previousCount = computed(() =>
  recordStore.logLoaded && !recordStore.isFiltered ? recordStore.total : null,
);

const showQueryPanel = computed(() => queryPanelOpen.value || !store.hasRun);
const showPreview = computed(
  () =>
    Boolean(store.preview) ||
    store.previewLoading ||
    Boolean(store.previewError),
);

const PREVIEW_COLUMN = '520px';

const gridColumns = computed(() =>
  showPreview.value ? `minmax(0, 1fr) ${PREVIEW_COLUMN}` : 'minmax(0, 1fr)',
);

/**
 * An unsaved sheet still names who ran it and when: firms that print and file the
 * paper outside LeanLaw rely on the sheet alone.
 */
const unsavedSheet = computed<ConflictPrintSheet>(() => ({
  firmName: me.value?.company?.name ?? '',
  runByName: me.value?.name ?? '',
  runAt: store.ranAt,
  saved: false,
  queryText: store.ranQuery,
  parties: store.searchableParties.map((party) => party.text),
  includedTimeEntries: store.ranWithTimeEntries,
  hiddenRowCount: store.hiddenRowCount,
}));
const ignoredRoleWords = computed(() => {
  const seen: Record<string, true> = {};
  const words: string[] = [];
  (store.result?.parties ?? []).forEach((party) => {
    party.ignoredRoleWords.forEach((word) => {
      if (!seen[word]) {
        seen[word] = true;
        words.push(word);
      }
    });
  });
  return words;
});

/**
 * Set when the next unmount is a move within the check or out to one of its records,
 * so what the reviewer has on screen is still here when they come back (EC-003).
 */
let keepState = false;
/** Set once the reviewer has agreed to lose their notes, so the retry is not stopped. */
let leaveConfirmed = false;
let stopWatchingRoutes: (() => void) | null = null;

/** Result records open under clients (matters are nested there); going there is review. */
const isRecordRoute = (name: string): boolean => name.startsWith('clients.');

function onStateChangeStart(
  event: StateChangeEvent,
  toState: RouteTarget,
  toParams: Record<string, unknown>,
): void {
  const name = toState?.name ?? '';
  const staysInCheck = name === 'conflict' || isRecordRoute(name);
  // Notes typed into a saved check live only in its view, which a move remounts.
  const wouldLoseNotes =
    uiState.savedNotesDirty || (store.hasUnsavedNotes && !staysInCheck);
  if (wouldLoseNotes && !leaveConfirmed) {
    event.preventDefault();
    confirmLeave(name, toParams);
    return;
  }
  // Confirming only gives up the notes the dialog warned about; a move within the
  // check still keeps the new-check draft.
  keepState = staysInCheck;
}

async function confirmLeave(
  name: string,
  params: Record<string, unknown>,
): Promise<void> {
  try {
    const leave = await leaveDialog.value?.open();
    if (!leave) return;
    leaveConfirmed = true;
    uiState.savedNotesDirty = false;
    routeService.go(name, params);
  } catch (error) {
    handleError(error);
  }
}

function onBeforeUnload(event: BeforeUnloadEvent): void {
  if (!store.hasUnsavedNotes && !uiState.savedNotesDirty) return;
  event.preventDefault();
  // Chrome still requires returnValue to show the prompt.
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
}

async function loadSavedCheck(): Promise<void> {
  if (!savedCheckId.value) return;
  try {
    await recordStore.loadRecord(savedCheckId.value);
  } catch (error) {
    // A missing check has its own state (ERR-010); only other failures need a reason.
    if (!recordStore.recordNotFound) handleError(error);
  }
}

async function loadPreviousCount(): Promise<void> {
  try {
    await recordStore.loadLog();
  } catch {
    // The count is a nicety on the tab; the log itself reports its own failure.
  }
}

onMounted(async () => {
  const rootScope = props.rootScope as
    | { $on?: (name: string, fn: typeof onStateChangeStart) => () => void }
    | undefined;
  if (typeof rootScope?.$on === 'function') {
    stopWatchingRoutes = rootScope.$on('$stateChangeStart', onStateChangeStart);
  }
  window.addEventListener('beforeprint', expandPartialMatches);
  window.addEventListener('beforeunload', onBeforeUnload);
  if (view.value === 'saved') loadSavedCheck();
  if (view.value === 'new') loadPreviousCount();
  try {
    await meStore.getMe();
    store.setBillingInstructionsAccess(
      Boolean(me.value?.permissions?.matters?.billingInstructions),
    );
  } catch (error) {
    handleError(error);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeprint', expandPartialMatches);
  window.removeEventListener('beforeunload', onBeforeUnload);
  if (stopWatchingRoutes) stopWatchingRoutes();
  store.closePreview();
  // Results and notes hold party names and matter content; they go with the page
  // unless the reviewer is only stepping out to a record or to another tab.
  if (!keepState) {
    store.reset();
    recordStore.reset();
  }
});

/** A collapsed panel prints as nothing, so partial matches would silently vanish. */
function expandPartialMatches(): void {
  if (resultList.value) resultList.value.expandPartials();
  if (savedView.value) savedView.value.expandPartials();
}

async function run(): Promise<void> {
  queryPanelOpen.value = false;
  try {
    await store.runCheck();
  } catch (error) {
    // The page renders its own error state; the toast is for the reason why.
    handleError(error);
  }
}

function refine(): void {
  queryPanelOpen.value = true;
}

function onRemoveParty(index: number): void {
  store.query = removeParty(index);
  queryPanelOpen.value = true;
}

async function rerunWithTimeEntries(): Promise<void> {
  store.includeTimeEntries = true;
  try {
    await run();
  } catch (error) {
    handleError(error);
  }
}

function print(): void {
  expandPartialMatches();
  trackPrinted();
  window.print();
}

// EV-006: frontend-emitted because printing never reaches the server.
function trackPrinted(): void {
  if (!props.tracking) return;
  const checkId = view.value === 'saved' ? recordStore.record?.id : null;
  EventTracker.trackEvent(
    props.tracking,
    TrackingCategory.CONFLICT_CHECK,
    TrackingAction.PRINTED,
    checkId ? { checkId } : {},
  );
}

const conflictTabs: TabItem[] = [
  { value: CONFLICT_TAB_NEW, label: 'New check', dataCy: 'conflict-tab-new' },
  { value: CONFLICT_TAB_PREVIOUS, label: 'Previous checks', dataCy: 'conflict-tab-previous' },
];

function openTab(tab: string): void {
  routeService.go('conflict', {
    tab: tab === CONFLICT_TAB_PREVIOUS ? CONFLICT_TAB_PREVIOUS : null,
    check: null,
  });
}

function openSavedCheck(id: string): void {
  routeService.go('conflict', { tab: null, check: id });
}

const checkHref = (id: string): string | null =>
  routeService.href('conflict', { tab: null, check: id }, { absolute: false });

function openSavedPreview(row: ConflictResultRow): void {
  store.openPreview(row, recordStore.allRows);
}

async function saveCheck(): Promise<void> {
  if (notesOverLimit.value) return;
  try {
    const saved = await store.save();
    recordStore.setRecord(saved);
    store.reset();
    handleSuccess('Conflict check saved.');
    openSavedCheck(saved.id);
  } catch (error) {
    // ERR-008: the results and notes stay on screen; the banner says it did not save.
    handleError(error);
  }
}

/**
 * Opening a record leaves the check behind, and a reviewer clearing seven parties
 * does not want to lose the results to look one of them up — so these are links to
 * a new tab, built by ui-router rather than assembled by hand.
 */
const previewClientHref = computed(() => {
  const preview = store.preview;
  if (!preview) return null;
  const clientId =
    preview.type === 'client' ? preview.id : preview.client?.id ?? null;
  // `clients.client` redirects to the overview; a reviewer opening a conflict
  // result wants the record itself, which is the info tab.
  return clientId
    ? routeService.href('clients.client.view.info', { id: clientId })
    : null;
});

const previewMatterHref = computed(() => {
  const preview = store.preview;
  if (!preview || preview.type !== 'matter') return null;
  const clientId = preview.client?.id;
  // The matter route is nested under its client, so both ids are needed.
  return clientId
    ? routeService.href('clients.client.matter', {
        id: clientId,
        matterId: preview.id,
      })
    : null;
});

// A new query invalidates the run that produced the results on screen.
watch(
  () => store.query,
  (next) => {
    if (store.hasRun && next !== store.ranQuery) queryPanelOpen.value = true;
  },
);
</script>
